import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Receipt, Check, X, Eye } from 'lucide-react';

/**
 * ReceiptApprovalsPanel — admin queue of pending payment-receipt uploads.
 * Approve → auto-credits classes_purchased on the student's vertical.
 * Reject → marks rejected with optional reason.
 */
export default function ReceiptApprovalsPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === 'pending' ? '/receipts/pending' : `/receipts/all?status=${tab}`;
      const res = await api.get(url);
      setRows(res.data.receipts || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const reason = action === 'reject' ? (window.prompt('Reason (optional):') || '') : '';
    setBusy(id);
    try {
      await api.post(`/receipts/${id}/${action}`, { reason });
      toast.success(action === 'approve' ? 'Approved · Classes credited' : 'Rejected');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const token = localStorage.getItem('token');
  const backend = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="p-6 space-y-4" data-testid="receipt-approvals-panel">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
          <Receipt className="w-6 h-6 text-emerald-600" /> Receipt Approvals
        </h1>
        <div className="flex gap-1">
          {['pending', 'approved', 'rejected'].map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-1 rounded-full text-xs font-bold border ${tab === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
              data-testid={`tab-${t}`}
            >{t.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-4">No {tab} receipts.</p>
      ) : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className={`nb-card p-4 border-l-4 ${r.status === 'approved' ? 'border-l-emerald-500' : r.status === 'rejected' ? 'border-l-red-400' : 'border-l-amber-400'}`} data-testid={`receipt-${r.id}`}>
              <div className="flex items-start gap-4">
                <a
                  href={`${backend}/api/uploads/${r.file_id}?auth=${token}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-24 h-24 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0 flex items-center justify-center bg-slate-50 hover:border-indigo-400 transition"
                  data-testid={`view-file-${r.id}`}
                >
                  <img src={`${backend}/api/uploads/${r.file_id}?auth=${token}`} alt="receipt" className="w-full h-full object-cover" onError={e => { e.currentTarget.replaceWith(Object.assign(document.createElement('div'), { className: 'text-slate-400 text-xs flex items-center justify-center h-full', innerText: 'PDF/File' })); }} />
                </a>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900">{r.student_name}</p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    <b>{r.currency} {r.amount}</b> · {r.vertical} · <b>{r.num_classes}</b> classes
                  </p>
                  {r.note && <p className="text-xs text-slate-500 mt-1 italic">&ldquo;{r.note}&rdquo;</p>}
                  <p className="text-[11px] text-slate-400 mt-1">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                  {r.status !== 'pending' && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      {r.status === 'approved' && r.approved_by_name && `Approved by ${r.approved_by_name}`}
                      {r.status === 'rejected' && r.reason && `Reason: ${r.reason}`}
                    </p>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-1.5">
                    <Button size="sm" onClick={() => decide(r.id, 'approve')} disabled={busy === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-receipt-${r.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(r.id, 'reject')} disabled={busy === r.id} className="border-red-500 text-red-700 hover:bg-red-50 text-xs" data-testid={`reject-receipt-${r.id}`}>
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
